import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useAppConfig } from '../hooks/useAppConfig'
import { recordLoginEvent } from '../hooks/useSecurity'
import { useRateLimit } from '../hooks/useRateLimit'
import { useRiskEngine, type RiskScore, type CaptchaConfig } from '../hooks/useRiskEngine'
import { CaptchaChallenge } from '../components/CaptchaChallenge'
import { supabase } from '../lib/supabase'
import { getAuthErrorMessage } from '../lib/authErrors'
import { OAUTH_PROVIDERS, type OAuthProvider } from '../types/database'
import {
  Input,
  Button,
  VStack,
  HStack,
  Text,
  Heading,
  Banner,
  Divider,
  Skeleton,
  OTPInput,
  Checkbox,
  useMediaQuery
} from 'wss3-forge'
import { Mail20Regular, Key20Regular, ShieldCheckmark20Regular, ArrowLeft20Regular, Link20Regular, Fingerprint20Regular, ShieldLock20Regular } from '@fluentui/react-icons'

type LoginStep = 'email' | 'password' | 'choose-method' | 'otp' | 'recovery' | 'forgot' | 'captcha' | 'sso-redirect'

// Convert base64url to standard base64 for atob()
function base64urlToBase64(base64url: string): string {
  let base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  while (base64.length % 4) {
    base64 += '='
  }
  return base64
}

export function Login() {
  const navigate = useNavigate()
  const { user, signInWithOAuth, setMfaPending, mfaPending } = useAuth()
  const { app, redirectUri, loading: appLoading, error: appError, isDefaultApp } = useAppConfig()
  const { checkRateLimit, recordAttempt, formatBlockedMessage } = useRateLimit()
  const { calculateRisk, getCaptchaConfig, verifyCaptcha, shouldShowCaptcha, getRiskLevel } = useRiskEngine()
  const isLargeScreen = useMediaQuery('(min-width: 1024px)')
  usePageTitle('Login', app?.name)

  // Auto-redirect state for already logged-in users
  const [autoRedirecting, setAutoRedirecting] = useState(false)

  // Form state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // MFA state
  const [step, setStep] = useState<LoginStep>('email')
  const [otpCode, setOtpCode] = useState('')
  const [factorId, setFactorId] = useState('')
  const [challengeId, setChallengeId] = useState('')
  const [verifyingOtp, setVerifyingOtp] = useState(false)

  // Forgot password state
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotSuccess, setForgotSuccess] = useState(false)
  const [sendingReset, setSendingReset] = useState(false)

  // Recovery code state
  const [recoveryCode, setRecoveryCode] = useState('')
  const [verifyingRecovery, setVerifyingRecovery] = useState(false)

  // Remember me state
  const [rememberMe, setRememberMe] = useState(false)

  // Magic link state
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [sendingMagicLink, setSendingMagicLink] = useState(false)

  // Passkey state
  const [authenticatingPasskey, setAuthenticatingPasskey] = useState(false)
  const [passkeySupported, setPasskeySupported] = useState(false)
  const [, setHasPasskeys] = useState(false)
  const [checkingEmail, setCheckingEmail] = useState(false)

  // Captcha state
  const [, setCaptchaRequired] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  const [captchaConfig, setCaptchaConfig] = useState<CaptchaConfig | null>(null)
  const [currentRiskScore, setCurrentRiskScore] = useState<RiskScore | null>(null)
  const [verifyingCaptcha, setVerifyingCaptcha] = useState(false)

  // Enterprise SSO state
  const [, setSsoRedirecting] = useState(false)
  const [ssoOrganization, setSsoOrganization] = useState<string | null>(null)

  // Check if passkey is supported
  useEffect(() => {
    const checkPasskeySupport = async () => {
      if (window.PublicKeyCredential) {
        try {
          const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
          setPasskeySupported(available)
        } catch {
          setPasskeySupported(false)
        }
      }
    }
    checkPasskeySupport()
  }, [])

  // Get OAuth/PKCE params from URL (passed by OAuthAuthorize)
  const getOAuthParams = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const state = urlParams.get('state') || undefined
    const codeChallenge = urlParams.get('code_challenge') || undefined
    const codeChallengeMethod = urlParams.get('code_challenge_method') || 'S256'
    return { state, codeChallenge, codeChallengeMethod }
  }

  // Auto-redirect if user is already logged in (but not during MFA verification)
  useEffect(() => {
    if (user && !appLoading && !mfaPending) {
      // User is already logged in and MFA is complete
      if (app && redirectUri && !isDefaultApp) {
        // Redirect to app with auth code
        setAutoRedirecting(true)
        const { state, codeChallenge, codeChallengeMethod } = getOAuthParams()
        supabase.rpc('generate_auth_code', {
          p_user_id: user.id,
          p_app_id: app.id,
          p_redirect_uri: redirectUri,
          p_code_challenge: codeChallenge || null,
          p_code_challenge_method: codeChallenge ? codeChallengeMethod : null,
          p_state: state || null
        }).then(({ data, error }) => {
          if (error) {
            console.error('Failed to generate auth code:', error)
            setAutoRedirecting(false)
            return
          }
          const url = new URL(redirectUri)
          url.searchParams.set('code', data)
          if (state) url.searchParams.set('state', state)
          window.location.href = url.toString()
        })
      } else {
        // No app redirect, go to home
        navigate('/')
      }
    }
  }, [user, app, redirectUri, isDefaultApp, appLoading, mfaPending, navigate])

  const handleAuthSuccess = async (userId: string, authMethod: string = 'email') => {
    // Record login event
    await recordLoginEvent(userId, authMethod)

    if (app && redirectUri && !isDefaultApp) {
      try {
        const { state, codeChallenge, codeChallengeMethod } = getOAuthParams()
        const { data, error: codeError } = await supabase.rpc('generate_auth_code', {
          p_user_id: userId,
          p_app_id: app.id,
          p_redirect_uri: redirectUri,
          p_code_challenge: codeChallenge || null,
          p_code_challenge_method: codeChallenge ? codeChallengeMethod : null,
          p_state: state || null
        })

        if (codeError) throw codeError

        const redirectUrl = new URL(redirectUri)
        redirectUrl.searchParams.set('code', data)
        if (state) redirectUrl.searchParams.set('state', state)
        window.location.href = redirectUrl.toString()
        return
      } catch (err) {
        console.error('Failed to generate auth code:', err)
        setError('Something went wrong while completing your sign in. Please try again.')
        setLoading(false)
        return
      }
    }

    navigate('/')
  }

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setCheckingEmail(true)

    // Validate email
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address')
      setCheckingEmail(false)
      return
    }

    // Check rate limit
    const rateLimitResult = await checkRateLimit(email, 'login')
    if (!rateLimitResult.allowed) {
      setError(`Too many attempts. ${formatBlockedMessage(rateLimitResult.blockedUntil)}`)
      setCheckingEmail(false)
      return
    }

    try {
      // Check for enterprise SSO if we have an app context
      if (app && !isDefaultApp) {
        const { data: ssoData, error: ssoError } = await supabase.functions.invoke('sso-init', {
          body: {
            email,
            app_id: app.id,
            redirect_uri: redirectUri
          }
        })

        if (!ssoError && ssoData?.sso_available) {
          // Enterprise SSO is available for this domain
          setSsoOrganization(ssoData.organization_name)
          setSsoRedirecting(true)
          setStep('sso-redirect')

          // Store SSO state for callback
          sessionStorage.setItem('sso_state', JSON.stringify({
            app_id: app.id,
            redirect_uri: redirectUri,
            connection_id: ssoData.connection_id,
            protocol: ssoData.protocol
          }))

          if (ssoData.protocol === 'oidc') {
            sessionStorage.setItem('sso_oidc_state', ssoData.state)
          }

          // Small delay for UI feedback then redirect
          setTimeout(() => {
            window.location.href = ssoData.redirect_url
          }, 1500)

          return
        }
      }

      // Check if user has passkeys
      const { data: options } = await supabase.functions.invoke('passkey-auth', {
        body: { action: 'options', email }
      })

      const userHasPasskeys = options?.allowCredentials?.length > 0
      setHasPasskeys(userHasPasskeys)

      if (userHasPasskeys && passkeySupported) {
        setStep('choose-method')
      } else {
        setStep('password')
      }
    } catch {
      // If error (user doesn't exist, etc), go to password step
      setStep('password')
    } finally {
      setCheckingEmail(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    // Check rate limit before attempting login
    const rateLimitResult = await checkRateLimit(email, 'login')
    if (!rateLimitResult.allowed) {
      setError(`Too many login attempts. ${formatBlockedMessage(rateLimitResult.blockedUntil)}`)
      setLoading(false)
      return
    }

    // Check risk score and captcha requirements (only if captcha not already verified)
    if (!captchaToken && app && !isDefaultApp) {
      const config = await getCaptchaConfig(app.id)
      if (config?.enabled && config.site_key) {
        setCaptchaConfig(config)

        // Calculate risk score
        const riskScore = await calculateRisk(email)
        setCurrentRiskScore(riskScore)

        // If risk is high enough, require captcha
        if (riskScore && shouldShowCaptcha(riskScore, config)) {
          setCaptchaRequired(true)
          setStep('captcha')
          setLoading(false)
          return
        }
      }
    }

    // Set MFA pending BEFORE login to prevent redirect during check
    setMfaPending(true)

    try {
      // Sign in with password
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (signInError) {
        // Record failed attempt
        await recordAttempt(email, 'login', false)
        setError(getAuthErrorMessage(signInError.message))
        setMfaPending(false)
        setLoading(false)
        return
      }

      // Record successful attempt
      await recordAttempt(email, 'login', true)

      // Check if MFA is required by looking at AAL level
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()

      if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
        // User has MFA enabled and needs to verify
        const { data: factorsData } = await supabase.auth.mfa.listFactors()
        const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || []

        if (verifiedFactors.length > 0) {
          const factor = verifiedFactors[0]
          setFactorId(factor.id)

          // Create a challenge
          const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
            factorId: factor.id
          })

          if (challengeError) {
            setError(getAuthErrorMessage(challengeError.message))
            setMfaPending(false)
            setLoading(false)
            return
          }

          setChallengeId(challengeData.id)
          // mfaPending already true
          setStep('otp')
          setLoading(false)
          return
        }
      }

      // No MFA required, clear pending and proceed
      setMfaPending(false)
      if (data?.user) {
        await handleAuthSuccess(data.user.id)
      }
    } catch (err: any) {
      setError(getAuthErrorMessage(err.message))
      setMfaPending(false)
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (otpCode.length !== 6) {
      setError('Please enter a complete 6-digit verification code.')
      return
    }

    setError(null)
    setVerifyingOtp(true)

    try {
      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code: otpCode
      })

      if (verifyError) {
        setError(getAuthErrorMessage(verifyError.message))
        setVerifyingOtp(false)
        return
      }

      // MFA verified, clear pending state and proceed with success
      setMfaPending(false)
      if (data?.user) {
        await handleAuthSuccess(data.user.id)
      }
    } catch (err: any) {
      setError(getAuthErrorMessage(err.message))
      setVerifyingOtp(false)
    }
  }

  const handleBackToCredentials = async () => {
    // Sign out to clear the AAL1 session
    await supabase.auth.signOut({ scope: 'global' })
    setMfaPending(false)
    setStep('email')
    setOtpCode('')
    setFactorId('')
    setChallengeId('')
    setRecoveryCode('')
    setPassword('')
    setError(null)
  }

  const handleVerifyRecoveryCode = async () => {
    if (!recoveryCode.trim()) {
      setError('Please enter a recovery code')
      return
    }

    setError(null)
    setVerifyingRecovery(true)

    try {
      // Get current session to get user ID
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        setError('Session expired. Please log in again.')
        await handleBackToCredentials()
        return
      }

      // Verify the recovery code
      const { data: isValid, error: verifyError } = await supabase.rpc('use_mfa_recovery_code', {
        p_user_id: session.user.id,
        p_code: recoveryCode.trim()
      })

      if (verifyError) throw verifyError

      if (!isValid) {
        setError('Invalid recovery code. Please try again.')
        setVerifyingRecovery(false)
        return
      }

      // Unenroll MFA factor to allow access
      const { data: factorsData } = await supabase.auth.mfa.listFactors()
      const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || []

      for (const factor of verifiedFactors) {
        await supabase.auth.mfa.unenroll({ factorId: factor.id })
      }

      // MFA has been disabled, proceed with login
      setMfaPending(false)
      await handleAuthSuccess(session.user.id)
    } catch (err: any) {
      console.error('Recovery code error:', err)
      setError(err.message || 'Failed to verify recovery code')
      setVerifyingRecovery(false)
    }
  }

  const handleOAuthSignIn = (provider: 'google' | 'azure' | 'github' | 'discord') => {
    if (app && redirectUri && !isDefaultApp) {
      sessionStorage.setItem('webba_auth_app_id', app.id)
      sessionStorage.setItem('webba_auth_redirect_uri', redirectUri)
    }
    signInWithOAuth(provider)
  }

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Check rate limit before sending reset email
    const rateLimitResult = await checkRateLimit(forgotEmail, 'password_reset')
    if (!rateLimitResult.allowed) {
      setError(`Too many password reset requests. ${formatBlockedMessage(rateLimitResult.blockedUntil)}`)
      return
    }

    setSendingReset(true)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/auth/callback?type=recovery`
      })

      if (error) {
        await recordAttempt(forgotEmail, 'password_reset', false)
        setError(getAuthErrorMessage(error.message))
        setSendingReset(false)
        return
      }

      await recordAttempt(forgotEmail, 'password_reset', true)
      setForgotSuccess(true)
      setSendingReset(false)
    } catch (err: any) {
      await recordAttempt(forgotEmail, 'password_reset', false)
      setError(getAuthErrorMessage(err.message))
      setSendingReset(false)
    }
  }

  const handleBackToLogin = () => {
    setStep('email')
    setForgotEmail('')
    setForgotSuccess(false)
    setMagicLinkSent(false)
    setCaptchaToken(null)
    setCaptchaRequired(false)
    setHasPasskeys(false)
    setError(null)
  }

  const handleCaptchaVerify = async (token: string) => {
    setCaptchaToken(token)
    setVerifyingCaptcha(true)
    setError(null)

    try {
      // Verify the captcha token via edge function
      if (captchaConfig?.secret_key) {
        const isValid = await verifyCaptcha(token, captchaConfig.secret_key)
        if (!isValid) {
          setError('Captcha verification failed. Please try again.')
          setCaptchaToken(null)
          setVerifyingCaptcha(false)
          return
        }
      }

      // Captcha verified, proceed with login
      setVerifyingCaptcha(false)
      setStep('password')

      // Trigger form submission programmatically
      setTimeout(() => {
        const form = document.querySelector('form')
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
        }
      }, 100)
    } catch (err: any) {
      console.error('Captcha verification error:', err)
      setError('Failed to verify captcha. Please try again.')
      setCaptchaToken(null)
      setVerifyingCaptcha(false)
    }
  }

  const handleCaptchaError = () => {
    setError('Captcha failed to load. Please try again.')
    setCaptchaToken(null)
  }

  const handleCaptchaExpired = () => {
    setError('Captcha expired. Please complete the challenge again.')
    setCaptchaToken(null)
  }

  const handlePasskeyLogin = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setError(null)
    setAuthenticatingPasskey(true)

    try {
      // Check rate limit before attempting passkey login
      const rateLimitResult = await checkRateLimit(email, 'login')
      if (!rateLimitResult.allowed) {
        setError(`Too many login attempts. ${formatBlockedMessage(rateLimitResult.blockedUntil)}`)
        setAuthenticatingPasskey(false)
        return
      }

      // Get authentication options from edge function
      const { data: options, error: optionsError } = await supabase.functions.invoke('passkey-auth', {
        body: { action: 'options', email }
      })

      if (optionsError) {
        throw new Error(optionsError.message || 'Failed to get passkey options')
      }

      if (options?.error) {
        throw new Error(options.error)
      }

      if (!options.allowCredentials || options.allowCredentials.length === 0) {
        throw new Error('No passkeys found for this account')
      }

      // Prepare credentials for WebAuthn
      // Note: challenge is base64url, credential_id is standard base64
      const publicKeyOptions: PublicKeyCredentialRequestOptions = {
        challenge: Uint8Array.from(atob(base64urlToBase64(options.challenge)), c => c.charCodeAt(0)),
        rpId: options.rpId,
        timeout: options.timeout,
        userVerification: options.userVerification as UserVerificationRequirement,
        allowCredentials: options.allowCredentials.map((cred: any) => ({
          type: cred.type,
          id: Uint8Array.from(atob(cred.id), c => c.charCodeAt(0)), // credential_id is already standard base64
          transports: cred.transports
        }))
      }

      // Prompt user for passkey
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions
      }) as PublicKeyCredential

      if (!credential) {
        throw new Error('No credential returned')
      }

      const authResponse = credential.response as AuthenticatorAssertionResponse

      // Encode credential data for sending to server
      const credentialData = {
        id: credential.id,
        rawId: btoa(String.fromCharCode(...new Uint8Array(credential.rawId))),
        response: {
          clientDataJSON: btoa(String.fromCharCode(...new Uint8Array(authResponse.clientDataJSON))),
          authenticatorData: btoa(String.fromCharCode(...new Uint8Array(authResponse.authenticatorData))),
          signature: btoa(String.fromCharCode(...new Uint8Array(authResponse.signature))),
          userHandle: authResponse.userHandle ? btoa(String.fromCharCode(...new Uint8Array(authResponse.userHandle))) : null
        },
        type: credential.type
      }

      // Verify with edge function
      const { data: result, error: verifyFnError } = await supabase.functions.invoke('passkey-auth', {
        body: { action: 'verify', credential: credentialData }
      })

      if (verifyFnError) {
        // Try to extract detailed error
        let errorMsg = verifyFnError.message || 'Passkey verification failed'
        if (verifyFnError.context) {
          try {
            const body = await verifyFnError.context.json()
            errorMsg = body?.error || errorMsg
          } catch {}
        }
        throw new Error(errorMsg)
      }

      if (result?.error) {
        throw new Error(result.error)
      }

      if (!result.success || !result.token_hash) {
        throw new Error('Authentication failed')
      }

      // Exchange the token for a session
      const { error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: result.token_hash,
        type: 'magiclink'
      })

      if (verifyError) {
        throw verifyError
      }

      // Record successful attempt
      await recordAttempt(email, 'login', true)

      // Success - the auth state change will handle the redirect
      await handleAuthSuccess(result.user_id, 'passkey')

    } catch (err: any) {
      // Record failed attempt
      await recordAttempt(email, 'login', false)

      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled')
      } else {
        setError(err.message || 'Passkey authentication failed')
      }
    } finally {
      setAuthenticatingPasskey(false)
    }
  }

  const handleSendMagicLink = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    setError(null)
    setSendingMagicLink(true)

    // Use forgotEmail if set (from forgot password flow), otherwise use email
    const targetEmail = forgotEmail || email

    try {
      // Build redirect URL with app params if present
      let redirectUrl = `${window.location.origin}/auth/callback`
      if (app && redirectUri && !isDefaultApp) {
        redirectUrl += `?app=${app.app_key}&redirect_uri=${encodeURIComponent(redirectUri)}`
      }

      const { error } = await supabase.auth.signInWithOtp({
        email: targetEmail,
        options: {
          emailRedirectTo: redirectUrl
        }
      })

      if (error) {
        setError(getAuthErrorMessage(error.message))
        setSendingMagicLink(false)
        return
      }

      setMagicLinkSent(true)
      setSendingMagicLink(false)
    } catch (err: any) {
      setError(getAuthErrorMessage(err.message))
      setSendingMagicLink(false)
    }
  }

  // OAuth provider icons (inline SVG)
  const providerIcons: Record<string, JSX.Element> = {
    google: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
      </svg>
    ),
    azure: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M0 0h11.5v11.5H0z" fill="#F25022" />
        <path d="M12.5 0H24v11.5H12.5z" fill="#7FBA00" />
        <path d="M0 12.5h11.5V24H0z" fill="#00A4EF" />
        <path d="M12.5 12.5H24V24H12.5z" fill="#FFB900" />
      </svg>
    ),
    github: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z" />
      </svg>
    ),
    discord: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="#5865F2">
        <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
      </svg>
    )
  }

  const allowedProviders = app?.allowed_providers || ['email', 'google', 'azure']
  const hasEmailProvider = allowedProviders.includes('email')
  const oauthProviders = allowedProviders.filter(p => p !== 'email') as OAuthProvider[]

  const appImage = app?.image_url || '/default-auth-image.jpg'
  const serviceName = app?.name || 'Webba ID'
  const themeColor = app?.theme_color || '#A35BFF'
  const currentYear = new Date().getFullYear()

  // Show loading while auto-redirecting or loading app config
  if (appLoading || autoRedirecting) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex'
      }}>
        <div style={{
          width: isLargeScreen ? '45%' : '100%',
          display: 'flex',
          flexDirection: 'column',
          padding: isLargeScreen ? '3rem 2rem 2rem 3rem' : '2rem 1.5rem',
          backgroundColor: 'var(--bg-secondary)'
        }}>
          <Skeleton width={120} height={32} />
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
              <Skeleton width={60} height={16} />
              <Skeleton width={240} height={32} />
              <Skeleton width="100%" height={48} />
              <Skeleton width="100%" height={48} />
              <Skeleton width="100%" height={44} />
            </VStack>
          </div>
        </div>
        {isLargeScreen && (
          <div style={{
            width: '55%',
            backgroundColor: 'var(--bg-secondary)'
          }}>
            <Skeleton width="100%" height="100%" />
          </div>
        )}
      </div>
    )
  }

  // OTP Step
  const renderOtpStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={handleBackToCredentials}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back to login
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <HStack gap="sm" style={{ alignItems: 'center' }}>
          <ShieldCheckmark20Regular style={{ color: 'var(--brand-primary)' }} />
          <Text size="sm" color="muted">Two-Factor Authentication</Text>
        </HStack>
        <Heading level={1}>Enter verification code</Heading>
        <Text color="muted">
          Enter the 6-digit code from your authenticator app
        </Text>
      </VStack>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {/* OTP Input */}
      <form onSubmit={(e) => { e.preventDefault(); handleVerifyOtp(); }} style={{ width: '100%' }}>
        <VStack gap="md">
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            padding: '1.5rem',
            backgroundColor: 'var(--bg-tertiary)',
            borderRadius: 'var(--radius-lg)'
          }}>
            <OTPInput
              length={6}
              value={otpCode}
              onChange={setOtpCode}
              onComplete={handleVerifyOtp}
            />
          </div>

          <Button
            type="submit"
            fullWidth
            loading={verifyingOtp}
            disabled={verifyingOtp || otpCode.length !== 6}
          >
            Verify
          </Button>

          <Text size="sm" color="muted" style={{ textAlign: 'center' }}>
            Lost access to your authenticator?{' '}
            <span
              style={{ color: themeColor, cursor: 'pointer' }}
              onClick={() => { setStep('recovery'); setError(null); }}
            >
              Use a recovery code
            </span>
          </Text>
        </VStack>
      </form>
    </VStack>
  )

  // Recovery Code Step
  const renderRecoveryStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => { setStep('otp'); setRecoveryCode(''); setError(null); }}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back to OTP
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <HStack gap="sm" style={{ alignItems: 'center' }}>
          <Key20Regular style={{ color: 'var(--brand-primary)' }} />
          <Text size="sm" color="muted">Account Recovery</Text>
        </HStack>
        <Heading level={1}>Enter recovery code</Heading>
        <Text color="muted">
          Enter one of the recovery codes you saved when setting up 2FA
        </Text>
      </VStack>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      <form onSubmit={(e) => { e.preventDefault(); handleVerifyRecoveryCode(); }} style={{ width: '100%' }}>
        <VStack gap="md">
          <Input
            label="Recovery code"
            placeholder="XXXXX-XXXXX"
            value={recoveryCode}
            onChange={(v) => setRecoveryCode(v as string)}
            icon={<Key20Regular />}
          />

          <Button
            type="submit"
            fullWidth
            loading={verifyingRecovery}
            disabled={verifyingRecovery || !recoveryCode.trim()}
          >
            Verify & Sign In
          </Button>

          <Text size="xs" color="muted" style={{ textAlign: 'center' }}>
            Using a recovery code will disable 2FA on your account. You can re-enable it in your security settings.
          </Text>

          <Text size="xs" color="muted" style={{ textAlign: 'center', marginTop: '1rem' }}>
            Don't have recovery codes?{' '}
            <a href="mailto:help@webba-creative.com?subject=Account%20Recovery%20Request" style={{ color: 'var(--text-muted)' }}>
              Contact support
            </a>
          </Text>
        </VStack>
      </form>
    </VStack>
  )

  // Captcha Step
  const renderCaptchaStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={handleBackToLogin}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back to login
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <HStack gap="sm" style={{ alignItems: 'center' }}>
          <ShieldLock20Regular style={{ color: 'var(--brand-primary)' }} />
          <Text size="sm" color="muted">Security Check</Text>
        </HStack>
        <Heading level={1}>Verify you're human</Heading>
        <Text color="muted">
          We detected some unusual activity. Please complete this security check to continue.
        </Text>
      </VStack>

      {currentRiskScore && (
        <Banner variant="warning" title={`Risk Level: ${getRiskLevel(currentRiskScore.score).toUpperCase()}`}>
          {currentRiskScore.factors.length > 0 && (
            <VStack gap="xs" style={{ marginTop: '0.5rem' }}>
              {currentRiskScore.factors.map((factor, idx) => (
                <Text key={idx} size="sm">{factor.description}</Text>
              ))}
            </VStack>
          )}
        </Banner>
      )}

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {captchaConfig?.site_key && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          padding: '1.5rem',
          backgroundColor: 'var(--bg-tertiary)',
          borderRadius: 'var(--radius-lg)'
        }}>
          <CaptchaChallenge
            siteKey={captchaConfig.site_key}
            onVerify={handleCaptchaVerify}
            onError={handleCaptchaError}
            onExpired={handleCaptchaExpired}
          />
        </div>
      )}

      {verifyingCaptcha && (
        <Text size="sm" color="muted" style={{ textAlign: 'center' }}>
          Verifying...
        </Text>
      )}
    </VStack>
  )

  // Enterprise SSO Redirect Step
  const renderSsoRedirectStep = () => (
    <VStack gap="lg" style={{ width: '100%', maxWidth: '360px', alignItems: 'center', textAlign: 'center' }}>
      {/* SSO Icon */}
      <div style={{
        width: '80px',
        height: '80px',
        borderRadius: '50%',
        backgroundColor: 'var(--brand-primary-light)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <ShieldCheckmark20Regular style={{ fontSize: '40px', color: 'var(--brand-primary)' }} />
      </div>

      {/* Title */}
      <VStack gap="xs">
        <Text size="sm" color="muted">Enterprise SSO</Text>
        <Heading level={2}>Redirecting to your organization</Heading>
        {ssoOrganization && (
          <Text color="muted">
            You'll be redirected to <strong>{ssoOrganization}</strong>'s identity provider to sign in.
          </Text>
        )}
      </VStack>

      {/* Loading spinner */}
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid var(--border-primary)',
        borderTopColor: 'var(--brand-primary)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />

      <Text size="sm" color="muted">
        Please wait...
      </Text>

      {/* Cancel button */}
      <Button
        variant="ghost"
        onClick={() => {
          setSsoRedirecting(false)
          setSsoOrganization(null)
          setStep('email')
          sessionStorage.removeItem('sso_state')
          sessionStorage.removeItem('sso_oidc_state')
        }}
      >
        Cancel
      </Button>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </VStack>
  )

  // Forgot Password Step
  const renderForgotStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={handleBackToLogin}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back to login
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <Text size="sm" color="muted">Password Recovery</Text>
        <Heading level={1}>Reset your password</Heading>
        <Text color="muted">
          Enter your email address and we'll send you a link to reset your password.
        </Text>
      </VStack>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {forgotSuccess || magicLinkSent ? (
        <VStack gap="md" style={{ width: '100%' }}>
          <Banner variant="success" title={magicLinkSent ? "Check your inbox" : "Email sent"}>
            {magicLinkSent
              ? `We've sent a magic link to ${forgotEmail || email}. Click the link to sign in.`
              : "Check your inbox for a password reset link. It may take a few minutes to arrive."
            }
          </Banner>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleBackToLogin}
          >
            Back to login
          </Button>
        </VStack>
      ) : (
        <VStack gap="md" style={{ width: '100%' }}>
          <form onSubmit={handleForgotPassword} style={{ width: '100%' }} className="auth-form-compact">
            <VStack gap="md">
              <Input
                label="Email"
                type="email"
                placeholder="you@example.com"
                icon={<Mail20Regular />}
                value={forgotEmail}
                onChange={(value) => setForgotEmail(value as string)}
              />

              <Button
                type="submit"
                fullWidth
                loading={sendingReset}
                disabled={!forgotEmail || sendingReset}
              >
                Send reset link
              </Button>
            </VStack>
          </form>

          <Divider label="or" />

          <Button
            variant="secondary"
            fullWidth
            icon={<Link20Regular />}
            onClick={() => handleSendMagicLink()}
            loading={sendingMagicLink}
            disabled={!forgotEmail || sendingMagicLink}
          >
            Send magic link instead
          </Button>

          <Text size="xs" color="muted" style={{ textAlign: 'center' }}>
            Get a link to sign in without resetting your password
          </Text>
        </VStack>
      )}
    </VStack>
  )

  // Email Step - Just email input + OAuth
  const renderEmailStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Title */}
      <VStack gap="xs">
        <Text size="sm" color="muted">Login</Text>
        <Heading level={1}>Welcome to {serviceName}</Heading>
      </VStack>

      {appError && (
        <Banner variant="warning" title="Application not found">
          Continuing with default Webba ID login
        </Banner>
      )}

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      {/* Email Form */}
      {hasEmailProvider && (
        <form onSubmit={handleEmailContinue} style={{ width: '100%' }} className="auth-form-compact">
          <VStack gap="md">
            <Input
              label="Email"
              type="email"
              placeholder="you@example.com"
              icon={<Mail20Regular />}
              value={email}
              onChange={(value) => setEmail(value as string)}
              data-testid="email"
            />

            <Button
              type="submit"
              fullWidth
              loading={checkingEmail}
              data-testid="login-button"
            >
              Continue
            </Button>
          </VStack>
        </form>
      )}

      {/* OAuth Providers */}
      {oauthProviders.length > 0 && (
        <VStack gap="md" style={{ width: '100%' }}>
          {hasEmailProvider && <Divider label="or" />}

          <div style={{
            display: 'grid',
            gridTemplateColumns: oauthProviders.length === 1 ? '1fr' : '1fr 1fr',
            gap: '0.5rem',
            width: '100%'
          }}>
            {oauthProviders.map((provider, index) => {
              const providerInfo = OAUTH_PROVIDERS.find(p => p.id === provider)
              if (!providerInfo) return null

              const isLast = index === oauthProviders.length - 1
              const isOdd = oauthProviders.length % 2 === 1
              const shouldSpan = isLast && isOdd && oauthProviders.length > 1

              return (
                <Button
                  key={provider}
                  variant="secondary"
                  fullWidth
                  onClick={() => handleOAuthSignIn(provider as 'google' | 'azure' | 'github' | 'discord')}
                  style={shouldSpan ? { gridColumn: 'span 2' } : undefined}
                >
                  <HStack gap="sm" style={{ alignItems: 'center', justifyContent: 'center' }}>
                    {providerIcons[provider]}
                    <span>{providerInfo.label}</span>
                  </HStack>
                </Button>
              )
            })}
          </div>
        </VStack>
      )}

      {/* Terms and Privacy */}
      <Text size="xs" color="muted">
        By continuing, you agree to our{' '}
        <Link to="/legal/terms" style={{ color: 'var(--text-secondary)' }}>Terms of Service</Link>
        {' '}and{' '}
        <Link to="/legal/privacy" style={{ color: 'var(--text-secondary)' }}>Privacy Policy</Link>
      </Text>

      {/* Sign up link */}
      <Text size="md" color="muted" style={{ textAlign: 'center', marginTop: '1rem' }}>
        Don't have an account?{' '}
        <Link
          to={app && !isDefaultApp ? `/register?app=${app.app_key}${redirectUri ? `&redirect_uri=${encodeURIComponent(redirectUri)}` : ''}` : '/register'}
          style={{ color: themeColor }}
        >
          Sign up
        </Link>
      </Text>
    </VStack>
  )

  // Choose Method Step - Passkey vs Password
  const renderChooseMethodStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => { setStep('email'); setError(null); }}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <Text size="sm" color="muted">{email}</Text>
        <Heading level={1}>Choose how to sign in</Heading>
      </VStack>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      <VStack gap="sm" style={{ width: '100%' }}>
        <Button
          variant="primary"
          fullWidth
          icon={<Fingerprint20Regular />}
          onClick={() => handlePasskeyLogin()}
          loading={authenticatingPasskey}
        >
          Use passkey
        </Button>

        <Button
          variant="secondary"
          fullWidth
          icon={<Key20Regular />}
          onClick={() => setStep('password')}
        >
          Use password
        </Button>
      </VStack>

      <div style={{ textAlign: 'center' }}>
        <Text size="sm" color="muted">
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => { setStep('forgot'); setForgotEmail(email); }}
          >
            Forgot password?
          </span>
        </Text>
      </div>
    </VStack>
  )

  // Password Step - Password only
  const renderPasswordStep = () => (
    <VStack gap="md" style={{ width: '100%', maxWidth: '360px' }}>
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => { setStep('email'); setError(null); setPassword(''); }}
        icon={<ArrowLeft20Regular />}
        style={{ alignSelf: 'flex-start', marginLeft: '-0.75rem' }}
      >
        Back
      </Button>

      {/* Title */}
      <VStack gap="xs">
        <Text size="sm" color="muted">{email}</Text>
        <Heading level={1}>Enter your password</Heading>
      </VStack>

      {error && (
        <Banner variant="error" title="Error">
          {error}
        </Banner>
      )}

      <form onSubmit={handleSubmit} style={{ width: '100%' }} className="auth-form-compact">
        <VStack gap="md">
          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            icon={<Key20Regular />}
            value={password}
            onChange={(value) => setPassword(value as string)}
            data-testid="password"
          />

          <Checkbox
            checked={rememberMe}
            onChange={setRememberMe}
            label="Remember me"
          />

          <Button
            type="submit"
            fullWidth
            loading={loading}
            data-testid="login-button"
          >
            Sign in
          </Button>
        </VStack>
      </form>

      <div style={{ textAlign: 'center' }}>
        <Text size="sm" color="muted">
          <span
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => { setStep('forgot'); setForgotEmail(email); }}
          >
            Forgot password?
          </span>
        </Text>
      </div>
    </VStack>
  )

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      backgroundColor: 'var(--bg-primary)'
    }}>
      {/* Left Section - Form (45% desktop, 100% mobile) */}
      <div style={{
        width: isLargeScreen ? '45%' : '100%',
        display: 'flex',
        flexDirection: 'column',
        padding: isLargeScreen ? '3rem 2rem 2rem 3rem' : '2rem 1.5rem',
        backgroundColor: 'var(--bg-secondary)'
      }}>
        {/* Logo top left */}
        <div style={{ color: 'var(--text-primary)' }}>
          <svg height="32" viewBox="0 0 763 182" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M91.7937 90.8659L73.8155 142.238L29.5812 2.45244L29.2196 1.29688H0L55.7429 177.471L56.1123 178.627H91.2435L121.949 90.8659H91.7937Z" fill="currentColor" />
            <path d="M203.282 1.29688H174.07L118.649 176.473L117.965 178.627H147.169L202.598 3.4508L203.282 1.29688Z" fill="currentColor" />
            <path d="M745.245 153.637V80.4895C745.245 62.5191 730.624 47.8896 712.661 47.8896H658.074C646.573 47.8896 637.211 57.2522 637.211 68.7608V73.0451H717.865V100.26H682.129C673.23 100.26 665.023 101.714 657.744 104.584C650.268 107.453 644.262 111.942 639.907 117.908C635.363 123.812 633.154 131.217 633.154 140.532C633.154 149.848 635.332 156.978 639.797 163.015C644.105 169.131 650.087 173.911 657.594 177.228C664.764 180.27 672.876 181.811 681.704 181.811C690.532 181.811 697.93 180.64 703.589 178.234C709.674 175.664 714.092 172.676 717.496 168.817C718.714 167.417 719.854 166.034 720.9 164.674C721.89 167.763 723.596 170.42 726.088 172.755C730.058 176.481 735.395 178.368 741.967 178.368H763.027V153.629H745.237L745.245 153.637ZM717.842 124.401C717.535 134.464 714.383 142.38 708.456 147.969C702.379 153.873 694.133 156.868 683.953 156.868C677.027 156.868 671.359 155.28 666.65 152.017C662.814 149.25 660.951 145.296 660.951 139.927C660.951 134.558 662.649 131.138 666.249 128.576C670.581 125.809 676.131 124.401 682.75 124.401H717.857H717.842Z" fill="currentColor" />
            <path d="M586.61 55.7665L586.578 55.7507C580.093 52.0639 573.057 49.6427 565.66 48.5578C562.657 48.1334 559.466 47.9133 556.164 47.9133C555.881 47.9133 555.614 47.929 555.386 47.9526H536.621C524.948 47.9526 515.444 57.4802 515.444 69.1853V73.4696H551.392C562.453 73.4775 571.139 76.8892 577.963 83.8934C584.81 90.9133 588.276 100.92 588.276 113.648V115.88C588.276 128.914 584.904 138.717 577.963 145.839C571.131 152.859 562.343 156.27 551.109 156.27C539.876 156.27 530.993 152.749 524.224 145.799C517.314 138.889 513.958 129.102 513.958 115.88V0H486.177V178.383H513.345V163.597C516.387 167.323 520.239 170.75 525.058 174.005C532.714 179.193 543.185 181.819 556.164 181.819C567.114 181.819 577.287 179.177 586.39 173.981C595.383 168.84 602.725 161.223 608.212 151.341C613.754 141.531 616.443 130.132 616.443 116.478V113.239C616.443 99.8592 613.746 88.2091 608.432 78.6343C603.087 68.8001 595.949 61.3242 586.602 55.7586L586.61 55.7665Z" fill="currentColor" />
            <path d="M436.568 55.7665L436.536 55.7507C430.051 52.0639 423.015 49.6427 415.618 48.5578C412.615 48.1334 409.424 47.9133 406.122 47.9133C405.839 47.9133 405.579 47.929 405.344 47.9526H386.579C374.906 47.9526 365.409 57.4802 365.409 69.1853V73.4696H401.35C412.411 73.4775 421.097 76.8892 427.921 83.8934C434.768 90.9133 438.234 100.92 438.234 113.648V115.88C438.234 128.914 434.862 138.717 427.921 145.839C421.089 152.859 412.309 156.27 401.075 156.27C389.842 156.27 380.959 152.749 374.182 145.799C367.272 138.889 363.916 129.11 363.916 115.88V0H336.135V178.383H363.303V163.597C366.337 167.323 370.197 170.743 375.016 174.005C382.68 179.193 393.143 181.819 406.122 181.819C417.072 181.819 427.245 179.177 436.356 173.981C445.349 168.84 452.691 161.223 458.178 151.341C463.72 141.531 466.408 130.124 466.408 116.478V113.239C466.408 99.8592 463.712 88.217 458.398 78.6343C453.052 68.808 445.915 61.3242 436.568 55.7586V55.7665Z" fill="currentColor" />
            <path d="M288.274 51.8202C279.093 46.6947 268.409 44.1006 256.523 44.1006C244.637 44.1006 233.27 46.8677 223.751 52.3311C214.184 57.8024 206.59 65.6006 201.189 75.5134C195.859 85.3319 193.155 96.9034 193.155 109.913V112.822C193.155 125.659 195.859 137.152 201.189 146.971C206.606 156.907 214.278 164.721 224.01 170.192C233.663 175.593 244.936 178.344 257.53 178.383H281.938C295.137 178.383 305.875 167.645 305.875 154.446V152.788H258.214C247.727 152.788 238.899 149.525 231.981 143.087C226.007 137.6 222.422 130.006 221.298 120.518H317.95V107.469C317.95 94.7731 315.316 83.5396 310.128 74.0749C304.869 64.445 297.519 56.9613 288.274 51.8202ZM232.602 77.8717C238.718 72.4712 246.768 69.7355 256.523 69.7355C266.279 69.7355 273.999 72.4712 280.036 77.8639C285.161 82.4548 288.243 88.4449 289.422 96.1487H222.1C223.971 88.5235 227.5 82.3762 232.594 77.8796L232.602 77.8717Z" fill="currentColor" />
          </svg>
        </div>

        {/* Center content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          paddingBottom: step !== 'email' ? '5rem' : 0
        }}>
          {step === 'email' && renderEmailStep()}
          {step === 'password' && renderPasswordStep()}
          {step === 'choose-method' && renderChooseMethodStep()}
          {step === 'otp' && renderOtpStep()}
          {step === 'recovery' && renderRecoveryStep()}
          {step === 'forgot' && renderForgotStep()}
          {step === 'captcha' && renderCaptchaStep()}
          {step === 'sso-redirect' && renderSsoRedirectStep()}
        </div>

        {/* Bottom footer */}
        <VStack gap="xs" style={{ alignItems: 'center' }}>
          <Text size="sm" color="muted">One account for all services</Text>
          <Text size="xs" color="muted">{currentYear} - Webba Creative Technologies</Text>

        </VStack>
      </div>

      {/* Right Section - Image (55% desktop, hidden mobile) */}
      {isLargeScreen && (
        <div style={{
          width: '55%',
          backgroundImage: `url(${appImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'left center'
        }} />
      )}
    </div>
  )
}
